import { useEffect, useState } from 'react'

import { MAX_PARAMETERS } from '../../utilities/paletteColors'
import fetchWithAuth from '../../utilities/fetchWithAuth'
import { useNavigate } from 'react-router-dom'

export const useAnalysisChainForm = ({ uuid = null }) => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [analyses, setAnalyses] = useState([])
  const [dataTypes, setDataTypes] = useState([])
  const [errors, setErrors] = useState({})
  const [analysisChain, setAnalysisChain] = useState({
    name: '',
    description: '',
    is_auto_compatible: false,
    is_basic: false,
    analysis_ids: [],
    input_datatype_ids: [],
    analysis_parameters: {},
  })
  const [newParameter, setNewParameter] = useState({})

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const [analysesResponse, dataTypesResponse] = await Promise.all([
          fetchWithAuth('analyses'),
          fetchWithAuth('data-types'),
        ])

        setAnalyses(analysesResponse.data.data || [])
        setDataTypes(dataTypesResponse.data.data || [])

        if (uuid) {
          const { data, status } = await fetchWithAuth(
            `analysis-chains/${uuid}`,
          )

          setAnalysisChain({
            name: data.name || '',
            description: data.description || '',
            is_auto_compatible: data.is_auto_compatible || false,
            is_basic: data.is_basic || false,
            analysis_ids: data.analyses.map((a) => a._id),
            input_datatype_ids: data.inputDataTypes.map((dt) => dt._id),
            analysis_parameters: data.analysis_parameters || {},
          })
        }
      } catch (err) {
        console.error('Error fetching data:', err)
        setErrors({ form: 'Failed to load data. Please try again.' })
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [uuid])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setAnalysisChain((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }))
    }
  }

  const handleAnalysisChange = (index, analysisId) => {
    const selectedAnalysis = analyses.find((a) => a._id === analysisId)
    setAnalysisChain((prev) => {
      const newAnalysisIds = [...prev.analysis_ids]
      newAnalysisIds[index] = analysisId
      const newAnalysisParameters = {
        ...prev.analysis_parameters,
        [analysisId]: selectedAnalysis ? selectedAnalysis.parameters : {},
      }
      if (index === 0) {
        return {
          ...prev,
          analysis_ids: [analysisId],
          input_datatype_ids: [],
          analysis_parameters: newAnalysisParameters,
        }
      }
      return {
        ...prev,
        analysis_ids: newAnalysisIds,
        analysis_parameters: newAnalysisParameters,
      }
    })
  }

  const handleDataTypeChange = (_analysisId, value) => {
    setAnalysisChain((prev) => ({
      ...prev,
      input_datatype_ids: value,
    }))
  }

  const handleAddAnalysis = () => {
    setAnalysisChain((prev) => ({
      ...prev,
      analysis_ids: [...prev.analysis_ids, ''],
    }))
  }

  const handleRemoveAnalysis = (index) => {
    setAnalysisChain((prev) => {
      const newAnalysisIds = prev.analysis_ids.slice(0, index)
      return { ...prev, analysis_ids: newAnalysisIds }
    })
  }

  const handleParameterChange = (analysisId, paramName, value) => {
    setAnalysisChain((prev) => ({
      ...prev,
      analysis_parameters: {
        ...prev.analysis_parameters,
        [analysisId]: {
          ...prev.analysis_parameters[analysisId],
          [paramName]: value,
        },
      },
    }))
  }

  const handleRemoveParameter = (analysisId, paramName) => {
    setAnalysisChain((prev) => {
      const updatedParameters = { ...prev.analysis_parameters[analysisId] }
      delete updatedParameters[paramName]
      return {
        ...prev,
        analysis_parameters: {
          ...prev.analysis_parameters,
          [analysisId]: updatedParameters,
        },
      }
    })
  }

  const handleAddParameter = (analysisId) => {
    const { key, value } = newParameter[analysisId] || {}
    if (!key || !value) return
    const currentParams = analysisChain.analysis_parameters?.[analysisId] || {}
    if (Object.keys(currentParams).length >= MAX_PARAMETERS) return
    setAnalysisChain((prev) => ({
      ...prev,
      analysis_parameters: {
        ...prev.analysis_parameters,
        [analysisId]: {
          ...prev.analysis_parameters[analysisId],
          [key]: value,
        },
      },
    }))
    setNewParameter((prev) => ({
      ...prev,
      [analysisId]: { key: '', value: '' },
    }))
  }

  const handleNewParameterChange = (analysisId, key, value) => {
    setNewParameter((prev) => ({
      ...prev,
      [analysisId]: { ...prev[analysisId], [key]: value },
    }))
  }

  const validateForm = () => {
    const newErrors = {}
    if (analysisChain.analysis_ids.length === 0) {
      newErrors.analyses = 'At least one analysis must be configured.'
    }
    if (analysisChain.input_datatype_ids.length === 0) {
      newErrors.dataTypes = 'An input data type must be selected.'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e, isSaveAsNew = false) => {
    e.preventDefault()
    if (!validateForm()) return
    try {
      setSaving(true)
      setErrors({})
      const payload = { ...analysisChain }
      const method = uuid && !isSaveAsNew ? 'PUT' : 'POST'
      const url =
        uuid && !isSaveAsNew ? `analysis-chains/${uuid}` : 'analysis-chains'
      const response = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (method === 'POST') {
        navigate(`/analysis-chains/${response.data.uuid}`)
      } else {
        navigate(`/analysis-chains/${uuid}`)
      }
    } catch (err) {
      console.error('Error saving analysis chain:', err)
      setErrors({ form: 'Failed to save analysis chain. Please try again.' })
    } finally {
      setSaving(false)
    }
  }

  const lastAnalysisId =
    analysisChain.analysis_ids[analysisChain.analysis_ids.length - 1]
  const lastAnalysis = analyses.find((a) => a._id === lastAnalysisId)
  const lastAnalysisOutputDataType = dataTypes.find(
    (dt) => dt.analysisId === lastAnalysis?._id,
  )
  const hasAvailableSubsequentAnalyses =
    analyses.filter((currentAnalysis) =>
      currentAnalysis.input_datatype_ids.some(
        (dt) => dt === lastAnalysisOutputDataType?._id,
      ),
    ).length > 0

  return {
    loading,
    saving,
    errors,
    analysisChain,
    analyses,
    dataTypes,
    newParameter,
    hasAvailableSubsequentAnalyses,
    handleInputChange,
    handleAnalysisChange,
    handleDataTypeChange,
    handleAddAnalysis,
    handleRemoveAnalysis,
    handleParameterChange,
    handleRemoveParameter,
    handleAddParameter,
    handleNewParameterChange,
    handleSubmit,
  }
}
